import type { components } from '@connected/api-client/api.generated';
import type { RadioGroupOption } from '@connected/form-fields-v2/components/form/radio-group';

import { api } from '@connected/api-client';
import { validators } from '@connected/api-client/generated/validators.generated';
import { useAppForm } from '@connected/form-fields-v2/hooks/use-app-form';
import { portaalQueryOptions } from '@connected/portaal-sdk/queries/options';
import { Button } from '@connected/ui/components/button';
import { DropdownMenu, DropdownMenuContent, DropdownMenuItem, DropdownMenuTrigger } from '@connected/ui/components/dropdown-menu';
import { Popout, PopoutBody, PopoutContent, PopoutHeader, PopoutTitle } from '@connected/ui/components/popout';
import { Skeleton } from '@connected/ui/components/skeleton';
import { useQuery } from '@tanstack/react-query';
import { createFileRoute, Link, useSearch } from '@tanstack/react-router';
import { ChevronDown, Edit, PlusIcon, Trash2 } from 'lucide-react';
import { useMemo, useState } from 'react';
import { useTranslation } from 'react-i18next';
import { toast } from 'sonner';
import z from 'zod';

import { DataTable } from '@/components/data-table';
import { LoadingPage } from '@/components/loading-page';
import { logger } from '@/lib/logger';
import { queryClient } from '@/lib/query-client';
import { showConfirm } from '@/stores/confirmer';

const validTabsArray = [
    'DistributionBoard',
    'FinalCircuit',
    'DistributionBoardFile',
    'DistributionBoardDevice',
    'RisoMeasurement',
    'PVInverter',
    'PVString',
] as const;

export const Route = createFileRoute(
    '/_auth/dashboard/organizations/$organizationId/settings/recommendations/',
)({
    component: RouteComponent,
    validateSearch: z.object({
        tab: z.enum(validTabsArray).optional(),
        search: z.string().optional(),
    }),
});

type recommendationItemAdd = components['schemas']['CreateElectricalObservationStandardTextItemCommand'] & {
    ElectricalObservationStandardTextItemId: string;
};

function RecommendationModal({
    isOpen,
    setIsOpen,
    recommendation,
    tabs,
}: {
    isOpen: boolean;
    setIsOpen: (isOpen: boolean) => void;
    recommendation: recommendationItemAdd | null;
    tabs: RadioGroupOption<recommendationItemAdd['ObjectKind']>[];
}) {
    const { organizationId } = Route.useParams();
    const { t } = useTranslation('dashboard');
    const { tab } = useSearch({ strict: false });
    const [loading, setIsLoading] = useState<boolean>(false);

    const addNewRecommendation = api.useMutation(
        'post',
        '/api/v2/electrical/observationstandardtexts/items/create',
        {
            onSuccess: () => {
                queryClient.invalidateQueries(portaalQueryOptions.recommendations.listRecommendations({ organizationId })).then(() => {
                    toast.success(t('dashboard.settings.recommendations.message.createSuccess'));
                    setIsOpen(false);
                },
                );
            },
            onError: (error) => {
                logger.error(
                    {
                        system: 'CreateElectricalObservationStandardTextItemCommand',
                        error,
                    },
                    { apiHttpMethod: 'Post', apiUrl: '/api/v2/electrical/observationstandardtexts/items/create' },
                );
                toast.error(error?.Message ?? t('dashboard.general.messages.error'));
            },
            onMutate: () => setIsLoading(true),

        },
    );
    const updateRecommendation = api.useMutation(
        'put',
        '/api/v2/electrical/observationstandardtexts/items/edit',
        {
            onSuccess: () => {
                queryClient.invalidateQueries(portaalQueryOptions.recommendations.listRecommendations({ organizationId })).then(() => {
                    toast.success(t('dashboard.settings.recommendations.message.editSuccess'));
                    setIsOpen(false);
                },
                );
            },
            onError: (error) => {
                logger.error(
                    {
                        system: 'EditElectricalObservationStandardTextItemCommand',
                        error,
                    },
                    { apiHttpMethod: 'Put', apiUrl: '/api/v2/electrical/observationstandardtexts/items/edit' },
                );
            },
            onMutate: () => setIsLoading(true),
        },
    );

    const DEFAULT_VALUES: recommendationItemAdd = {
        ElectricalObservationStandardTextItemId: recommendation?.ElectricalObservationStandardTextItemId ?? '',
        Description: recommendation?.Description ?? '',
        Order: recommendation?.Order ?? null,
        ObjectKind: validTabsArray.includes(recommendation?.ObjectKind as typeof validTabsArray[number])
            ? recommendation?.ObjectKind as typeof validTabsArray[number]
            : (validTabsArray.includes(tab as typeof validTabsArray[number]) ? tab as typeof validTabsArray[number] : 'DistributionBoard'),
        OrganizationId: organizationId,
        TextKind: recommendation?.TextKind ?? 'Recommendation',
    };

    const form = useAppForm(
        {
            defaultValues: DEFAULT_VALUES,
            onSubmit: async (values) => {
                try {
                    if (!recommendation) {
                        await addNewRecommendation.mutateAsync({
                            body: {
                                OrganizationId: organizationId ?? '',
                                Description: values?.value?.Description ?? '',
                                TextKind: values?.value?.TextKind ?? '',
                                ObjectKind: values?.value?.ObjectKind ?? '',
                            },
                        });
                    }
                    else {
                        await updateRecommendation.mutateAsync({
                            body: {
                                Description: values.value.Description ?? '',
                                ElectricalObservationStandardTextItemId: values.value.ElectricalObservationStandardTextItemId ?? '',
                                Order: 1,
                            },
                        });
                    }
                }
                catch (error) {
                    toast.error(error instanceof Error ? error.message : String(error));
                }
            },
        },
    );

    function handleSubmit(event: React.FormEvent<HTMLFormElement>) {
        event.preventDefault();
        form.handleSubmit();
    }

    return (
        <Popout open={isOpen} onOpenChange={setIsOpen}>
            <PopoutContent>
                <PopoutHeader>
                    <PopoutTitle>
                        {!recommendation
                            ? (
                                    t('dashboard.settings.recommendations.create.title')
                                )
                            : t('dashboard.settings.recommendations.update.title')}
                    </PopoutTitle>
                </PopoutHeader>
                <PopoutBody>
                    <form onSubmit={handleSubmit}>
                        <form.AppField
                            name="Description"
                            validators={{
                                onChange: validators.createElectricalObservationStandardTextItemCommand.description,
                            }}
                            children={field => (
                                <field.Label>
                                    <field.Label.Title label={t('dashboard.settings.recommendations.table.recommandation')} />
                                    <field.Textarea placeholder={t('dashboard.settings.recommendations.table.recommandationPlaceholder')} />
                                    <field.ValidationError />
                                </field.Label>
                            )}
                        >
                        </form.AppField>
                        {!recommendation
                            ? (
                                    <form.AppField
                                        name="ObjectKind"
                                        validators={{
                                            onChange: validators.createElectricalObservationStandardTextItemCommand.objectKind,
                                        }}
                                        children={field => (
                                            <field.Label asDiv>
                                                <field.Label.Title label={t('dashboard.settings.recommendations.table.typeRecommendation')} />
                                                <field.Popover closeOnChange={true}>
                                                    <field.RadioGroup options={tabs}>
                                                        <field.Popover.Trigger asChild>
                                                            <field.RadioGroup.Trigger placeholder={t('dashboard.users.table.rolePlaceholder')} />
                                                        </field.Popover.Trigger>
                                                        <field.Popover.Content className="h-auto min-h-30 [&>div]:flex-col [&>div]:items-start [&>div]:gap-6" matchTriggerWidth>
                                                            <field.RadioGroup options={tabs} />
                                                        </field.Popover.Content>
                                                    </field.RadioGroup>
                                                </field.Popover>
                                                <field.ValidationError />
                                            </field.Label>
                                        )}
                                    >
                                    </form.AppField>
                                )
                            : null}
                        <div className="w-full flex flex-row gap-2 mt-5">
                            <Button className="w-full" onClick={() => setIsOpen(false)} variant="outline">{t('dashboard.cancel')}</Button>
                            <Button disabled={loading} type="submit" variant="default" className="w-full">{t('dashboard.create')}</Button>
                        </div>
                    </form>
                </PopoutBody>
            </PopoutContent>
        </Popout>
    );
}
function RouteComponent() {
    const { organizationId } = Route.useParams();
    const { t } = useTranslation('dashboard');
    const search = Route.useSearch() as any;
    const tabsKind: RadioGroupOption<recommendationItemAdd['ObjectKind']>[] = [
        {
            value: 'DistributionBoard',
            label: t('dashboard.settings.recommendations.tabs.distributionBoard'),
        },
        {
            value: 'FinalCircuit',
            label: t('dashboard.settings.recommendations.tabs.finalCircuit'),
        },
        {
            value: 'PVInverter',
            label: t('dashboard.settings.recommendations.tabs.PVInverter'),
        },
    ];

    const [isOpenModal, setOpenModal] = useState<boolean>(false);
    const [selectedRecommendation, setSelectedRecommendation] = useState<recommendationItemAdd | null>(null);
    const [activeTab, setActiveTab] = useState<string>(tabsKind[0].value);
    const recommendationList = useQuery(
        portaalQueryOptions.recommendations.listRecommendations({ organizationId }),
    );

    if (recommendationList.isFetched && recommendationList.isError) {
        const error = recommendationList.error;
        logger.error(
            {
                system: 'DeleteElectricalInspectionNormItemCommand',
                error,
            },
            { apiHttpMethod: 'Get', apiUrl: '/api/v2/electrical/observationstandardtexts/items/list' },
        );
    };

    const removeRecommendation = api.useMutation(
        'delete',
        '/api/v2/electrical/observationstandardtexts/items/delete',
        {
            onSuccess: () => {
                queryClient.invalidateQueries(portaalQueryOptions.recommendations.listRecommendations({ organizationId })).then(() => {
                    toast.success(t('dashboard.settings.recommendations.message.removeSuccess'));
                },
                );
            },
            onError: (error) => {
                logger.error(
                    {
                        system: 'DeleteElectricalObservationStandardTextItemCommand',
                        error,
                    },
                    { apiHttpMethod: 'Delete', apiUrl: '/api/v2/electrical/observationstandardtexts/items/delete' },
                );
                toast.error(error?.Message ?? t('dashboard.general.messages.error'));
            },
        },
    );

    const switchTab = (tab: string) => {
        setActiveTab(tab);
    };

    const columns = useMemo(() => [
        {
            accessorKey: 'Aanbeveling',
            header: () => (
                <DataTable.Header className="max-w-52">
                    {t('dashboard.settings.recommendations.table.recommandation')}
                </DataTable.Header>
            ),
            cell: ({ row }: { row: { original: recommendationItemAdd } }) => (
                <DataTable.Cell variant="medium">
                    {row?.original?.Description}
                </DataTable.Cell>
            ),
        },
        {
            id: 'actionModal',
            meta: {
                sticky: true,
                width: 100,
            },
            header: '',
            cell: ({ row }: { row: { original: recommendationItemAdd } }) => {
                return (
                    <div className="flex gap-1 items-center justify-end ml-auto">
                        <Button
                            variant="secondary"
                            size="icon"
                            className="flex gap-2 w-fit h-fit justify-start p-2 bg-background-primary text-foreground hover:bg-background-secondary"
                            onClick={async () => {
                                if (await showConfirm(t('dashboard.settings.recommendations.message.confirmDelete'))) {
                                    removeRecommendation.mutateAsync({
                                        params: {
                                            query: {
                                                ElectricalObservationStandardTextItemId: row?.original?.ElectricalObservationStandardTextItemId,
                                            },
                                        },
                                    });
                                }
                            }}
                        >
                            <Trash2 className="text-text-primary/40 size-5" />
                        </Button>
                        <Button
                            variant="secondary"
                            size="icon"
                            className="flex gap-2 w-fit h-fit justify-start p-2 bg-background-primary text-foreground hover:bg-background-secondary"
                            onClick={() => {
                                setOpenModal(true);
                                setSelectedRecommendation(row?.original);
                            }}
                        >
                            <Edit className="text-text-primary/40 size-5" />
                        </Button>
                    </div>
                );
            },
        },
    ], [removeRecommendation, t]);

    const tableColumns = useMemo(
        () => recommendationList.isLoading
            ? columns.map(column => ({ ...column, cell: () => <Skeleton className="h-8 w-full" /> }))
            : columns,
        [columns, recommendationList.isLoading],
    );

    const tableData = useMemo<recommendationItemAdd[]>(
        () => {
            if (recommendationList.isError) {
                toast.error(recommendationList.error?.Message);
                return [];
            }
            const searchTerm = search.search?.toLowerCase() || '';

            return (
                recommendationList.data?.Items
                    ?.filter((norm) => {
                        if (norm.ObjectKind !== activeTab)
                            return false;
                        if (!searchTerm)
                            return true;

                        return [
                            norm.Description || '',
                        ].some(field => field?.toString().toLowerCase().includes(searchTerm));
                    })
                    .map(norm => ({
                        ElectricalObservationStandardTextItemId: norm.ElectricalObservationStandardTextItemId ?? '',
                        Description: norm.Description ?? '',
                        Order: norm.Order ?? null,
                        ObjectKind: norm.ObjectKind ?? 'DistributionBoard',
                        OrganizationId: organizationId,
                        TextKind: norm.TextKind ?? 'Recommendation',
                    }))
                    || []
            );
        },
        [recommendationList.isError, recommendationList.data?.Items, recommendationList.error?.Message, search.search, activeTab, organizationId],
    );

    if (recommendationList.isLoading) {
        return <LoadingPage />;
    }

    return (
        <div>
            <div className="flex flex-col md:flex-wrap gap-8 justify-start">
                <Button
                    variant="link"
                    className="flex sm:hidden items-end gap-1 border px-3 rounded-lg text-foreground hover:no-underline hover:bg-background"
                    onClick={() => {
                        setOpenModal(true);
                        setSelectedRecommendation(null);
                    }}
                >
                    <PlusIcon className="w-5 h-5" />
                    {t('dashboard.settings.recommendations.title')}
                </Button>
                <div className="w-full">
                    <DataTable.TableHead
                        title={t('dashboard.settings.recommendations.title')}
                        subtitle={t('dashboard.settings.recommendations.subtitle')}
                        search={Route}
                    />
                    <div>
                        {/* buttons lg+ */}
                        <div className="flex py-3 border px-5 w-full">
                            <div className="flex flex-col sm:flex-row md:gap-0 gap-2 w-full md:w-auto">
                                <div className="sm:hidden w-full">
                                    <DropdownMenu>
                                        <DropdownMenuTrigger className="flex items-center gap-2 border-2 border-primary/60 px-3 rounded-lg h-10 bg-background-primary text-primary hover:bg-primary/10 justify-center w-full">
                                            <span>{tabsKind.find(tab => tab.value === activeTab)?.label || tabsKind[0].label}</span>
                                            <ChevronDown className="w-5 h-5" />
                                        </DropdownMenuTrigger>
                                        <DropdownMenuContent className="flex flex-col w-[calc(100vw-2rem)]" align="start">
                                            {tabsKind.map(item => (
                                                <DropdownMenuItem
                                                    key={item.value}
                                                    className="p-3 focus:bg-background-secondary focus:text-popover-foreground cursor-pointer text-base"
                                                    asChild
                                                >
                                                    <Link
                                                        to="/dashboard/organizations/$organizationId/settings/recommendations"
                                                        params={{ organizationId }}
                                                        search={{
                                                            tab: item.value ?? undefined,
                                                        }}
                                                        onClick={() => switchTab(item.value ?? '')}
                                                    >
                                                        {item.label}
                                                    </Link>
                                                </DropdownMenuItem>
                                            ))}
                                        </DropdownMenuContent>
                                    </DropdownMenu>
                                </div>

                                {tabsKind.map((item, index) => (
                                    <Link
                                        key={item.value}
                                        to="/dashboard/organizations/$organizationId/settings/recommendations"
                                        params={{ organizationId }}
                                        search={{
                                            tab: item.value ?? undefined,
                                            search: search?.search,
                                        }}
                                        className="hidden sm:block"
                                    >
                                        <Button
                                            onClick={() => switchTab(item.value ?? '')}
                                            style={
                                                index === 0
                                                    ? { borderRadius: '10px 0px 0px 10px ', borderLeft: '1px solid hsl(var(--border-primary)' }
                                                    : index === tabsKind.length - 1
                                                        ? { borderRadius: '0px 10px 10px 0px' }
                                                        : { borderRadius: '0px' }
                                            }
                                            className={`border-border-primary border-t border-r border-b bg-background-active hover:bg-active ${item.value === activeTab ? ' text-secondary_hover font-bold' : 'text-secondary-700 bg-background-primary text-sm'}`}
                                        >
                                            {item.label}
                                        </Button>
                                    </Link>
                                ))}
                            </div>
                            <div className="flex flex-1 justify-end items-end">
                                <div className="sm:flex flex-row-reverse gap-4 hidden">
                                    <Button
                                        variant="link"
                                        className="flex items-center gap-1 border px-3 rounded-lg text-foreground hover:no-underline hover:bg-background"
                                        onClick={() => {
                                            setOpenModal(true);
                                            setSelectedRecommendation(null);
                                        }}
                                    >
                                        <PlusIcon className="w-5 h-5" />
                                        {t('dashboard.settings.recommendations.title')}
                                    </Button>
                                </div>
                            </div>
                        </div>

                    </div>
                    <DataTable
                        className="rounded-tl-none rounded-tr-none"
                        columns={tableColumns}
                        data={tableData}
                        pageSize={10}
                    />
                </div>
            </div>
            {isOpenModal
                ? (
                        <RecommendationModal isOpen={isOpenModal} tabs={tabsKind} setIsOpen={setOpenModal} recommendation={selectedRecommendation} />
                    )
                : null}
        </div>
    );
}
